import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle02Icon, Loading02Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/common/Bits';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { MotionSection } from '@/components/shared/MotionSection';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'via_draft_rapido';
const AUTOSAVE_DELAY = 2000; // ms

const DATA_TYPES = [
  { id: 'pacientes', label: 'Datos de pacientes (nombre, RUT, ficha clínica, exámenes)' },
  { id: 'financiera', label: 'Información financiera o de cobros' },
  { id: 'confidencial', label: 'Información confidencial de la institución' },
  { id: 'publicos', label: 'Solo datos públicos o ya anonimizados' },
  { id: 'no_seguro', label: 'No estoy seguro/a' },
];

const INITIATIVE_TYPES = [
  { id: 'nueva_herramienta', label: 'Quiero usar una herramienta de IA nueva (compra o contratación)' },
  { id: 'herramienta_existente', label: 'Quiero aplicar una herramienta ya autorizada a un caso nuevo' },
  { id: 'desarrollo_interno', label: 'Quiero desarrollar algo con IA internamente' },
  { id: 'piloto', label: 'Quiero probar algo en un piloto controlado' },
  { id: 'otra', label: 'Otra' },
];

const Field = ({ label, hint, children, required }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium text-foreground">
      {label}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
    {hint && <p className="text-xs text-muted-foreground -mt-1">{hint}</p>}
    {children}
  </div>
);

export default function QuickInitiativeForm() {
  const navigate = useNavigate();
  const { user, hasAccess } = useAuth();
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const autosaveTimer = useRef(null);

  const [f, setF] = useState({
    solicitante: '',
    area: '',
    correo: '',
    sponsor: '',
    tipo_iniciativa: [],
    tipo_otra: '',
    problema: '',
    que_haria_ia: '',
    beneficiarios: '',
    tipos_datos: [],
    impacto: '',
    datos_existentes: '',
    lider_uso: '',
    preocupaciones: '',
    contexto_adicional: '',
  });

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // --- Persistence: Load draft on mount ---
  useEffect(() => {
    // Try sessionStorage first (instant)
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setF(parsed.data || parsed);
        if (parsed.draftId) setDraftId(parsed.draftId);
      } catch {
        /* noop */
      }
    }

    // Then try API for server-persisted draft
    api
      .get('/initiative-drafts/active', { params: { mode: 'rapido' } })
      .then(({ data }) => {
        if (data && data.id) {
          setDraftId(data.id);
          const serverData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
          setF((prev) => ({ ...prev, ...serverData }));
          setLastSaved(new Date(data.updated_at));
        }
      })
      .catch(() => {
        /* no active draft, that's fine */
      });
  }, []);

  // --- Prefill user info ---
  useEffect(() => {
    if (user && !f.solicitante) {
      setF((prev) => ({
        ...prev,
        solicitante: prev.solicitante || user.name || '',
        correo: prev.correo || user.email || '',
      }));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Autosave to sessionStorage + API ---
  const saveDraft = useCallback(async (data, id) => {
    // Always save to sessionStorage (instant, survives refresh)
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ data, draftId: id }));

    // Save to API
    setSaving(true);
    try {
      if (id) {
        await api.put(`/initiative-drafts/${id}`, { data, step: 0 });
      } else {
        const { data: created } = await api.post('/initiative-drafts', {
          mode: 'rapido',
          data,
          step: 0,
        });
        setDraftId(created.id);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ data, draftId: created.id }));
      }
      setLastSaved(new Date());
    } catch {
      // Silent fail on autosave — sessionStorage has the backup
    } finally {
      setSaving(false);
    }
  }, []);

  // Debounced autosave on form change
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      saveDraft(f, draftId);
    }, AUTOSAVE_DELAY);

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [f, draftId, saveDraft]);

  // --- Submit: create demand intake from quick form ---
  const handleSubmit = async () => {
    // Validate minimum
    if (!f.problema.trim()) {
      toast.error('Cuéntanos qué problema o necesidad quieres resolver');
      return;
    }

    setSubmitting(true);
    try {
      // Build demand intake from quick form answers
      const demandIntake = {
        title: f.problema.slice(0, 120),
        description: [
          f.problema,
          f.que_haria_ia && `Lo que haría la IA: ${f.que_haria_ia}`,
          f.beneficiarios && `Beneficiarios: ${f.beneficiarios}`,
        ]
          .filter(Boolean)
          .join('\n\n'),
        origin: 'plataforma',
        source_contact: f.solicitante || user?.name || '',
        department: f.area || 'Por definir',
        urgency: 'Normal',
        notes: [
          f.impacto && `Impacto esperado: ${f.impacto}`,
          f.datos_existentes && `Datos existentes: ${f.datos_existentes}`,
          f.lider_uso && `Líder de uso: ${f.lider_uso}`,
          f.preocupaciones && `Preocupaciones: ${f.preocupaciones}`,
          f.contexto_adicional && `Contexto adicional: ${f.contexto_adicional}`,
          f.sponsor && `Patrocinador: ${f.sponsor}`,
          f.tipos_datos.length > 0 && `Tipos de datos: ${f.tipos_datos.join(', ')}`,
          f.tipo_iniciativa.length > 0 && `Tipo de iniciativa: ${f.tipo_iniciativa.join(', ')}`,
        ]
          .filter(Boolean)
          .join('\n'),
        provider_detected: false,
        intake_mode: 'rapido',
        extended_data: f,
      };

      await api.post('/demanda', demandIntake);

      // Mark draft as converted
      if (draftId) {
        await api
          .put(`/initiative-drafts/${draftId}`, {
            data: f,
            step: 0,
            status: 'completado',
          })
          .catch(() => {});
      }

      // Clean up sessionStorage
      sessionStorage.removeItem(STORAGE_KEY);

      toast.success('¡Solicitud registrada! El equipo de IA te contactará pronto.');
      // La bandeja de demanda no es accesible para todos los roles (p. ej. evaluador).
      // Redirigimos a la bandeja solo si el usuario tiene acceso; si no, al inicio.
      navigate(hasAccess('/demanda') ? '/demanda' : '/');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error al registrar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleArrayItem = (key, item) => {
    setF((prev) => {
      const arr = prev[key] || [];
      return {
        ...prev,
        [key]: arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item],
      };
    });
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <MotionSection>
        <PageHeader
          title="Formulario rápido"
          subtitle="Responde con tus palabras. No hay respuestas incorrectas."
          testId="quick-form-header"
          backTo="/demanda/nueva"
          backLabel="Volver a opciones"
        />
      </MotionSection>

      {/* Autosave indicator */}
      <div className="flex items-center justify-end gap-2 mb-4 min-h-[20px]">
        {saving && (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <HugeiconsIcon icon={Loading02Icon} size={12} strokeWidth={1.5} className="animate-spin" />
            Guardando...
          </span>
        )}
        {!saving && lastSaved && (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <HugeiconsIcon icon={Tick02Icon} size={12} strokeWidth={1.5} />
            Guardado
          </span>
        )}
      </div>

      <div className="space-y-8">
        {/* Section 1: Quién propone */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">1. ¿Quién propone?</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nombre del solicitante" required>
                <Input
                  value={f.solicitante}
                  onChange={(e) => set('solicitante', e.target.value)}
                  placeholder="Tu nombre"
                />
              </Field>
              <Field label="Área o gerencia">
                <Input
                  value={f.area}
                  onChange={(e) => set('area', e.target.value)}
                  placeholder="Ej: Dirección Médica"
                />
              </Field>
              <Field label="Correo">
                <Input
                  value={f.correo}
                  onChange={(e) => set('correo', e.target.value)}
                  placeholder="tu.correo@ucchristus.cl"
                  type="email"
                />
              </Field>
              <Field label="Patrocinador" hint="Gerente o subgerente que respalda la iniciativa">
                <Input
                  value={f.sponsor}
                  onChange={(e) => set('sponsor', e.target.value)}
                  placeholder="Nombre del patrocinador"
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Tipo de iniciativa */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">2. ¿Qué tipo de iniciativa es?</h3>
            <div className="space-y-2.5">
              {INITIATIVE_TYPES.map((type) => (
                <label key={type.id} className="flex items-start gap-2.5 cursor-pointer group">
                  <Checkbox
                    checked={f.tipo_iniciativa.includes(type.id)}
                    onCheckedChange={() => toggleArrayItem('tipo_iniciativa', type.id)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {type.label}
                  </span>
                </label>
              ))}
              {f.tipo_iniciativa.includes('otra') && (
                <Input
                  value={f.tipo_otra}
                  onChange={(e) => set('tipo_otra', e.target.value)}
                  placeholder="Describe el tipo de iniciativa"
                  className="mt-2 ml-7"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Cuéntanos tu idea */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">3. Cuéntanos tu idea</h3>
            <Field label="¿Qué problema o necesidad quieres resolver?" required>
              <Textarea
                value={f.problema}
                onChange={(e) => set('problema', e.target.value)}
                placeholder="Describe el problema que quieres abordar..."
                rows={3}
              />
            </Field>
            <Field label="¿Qué haría la IA en esta iniciativa?" hint="Describe con tus palabras">
              <Textarea
                value={f.que_haria_ia}
                onChange={(e) => set('que_haria_ia', e.target.value)}
                placeholder="Ej: Clasificar automáticamente los correos de pacientes..."
                rows={3}
              />
            </Field>
            <Field label="¿A quién beneficia?" hint="Pacientes, equipo interno, otro">
              <Textarea
                value={f.beneficiarios}
                onChange={(e) => set('beneficiarios', e.target.value)}
                placeholder="Ej: Equipo de atención al paciente y los pacientes mismos..."
                rows={2}
              />
            </Field>
          </CardContent>
        </Card>

        {/* Section 4: Tipo de información */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">4. ¿Qué tipo de información usaría?</h3>
            <p className="text-xs text-muted-foreground">Marca todo lo que aplique:</p>
            <div className="space-y-2.5">
              {DATA_TYPES.map((type) => (
                <label key={type.id} className="flex items-start gap-2.5 cursor-pointer group">
                  <Checkbox
                    checked={f.tipos_datos.includes(type.id)}
                    onCheckedChange={() => toggleArrayItem('tipos_datos', type.id)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {type.label}
                  </span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Para entender mejor */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">5. Algunas preguntas para entender mejor</h3>
            <p className="text-xs text-muted-foreground">No hay respuestas correctas ni incorrectas.</p>
            <Field label="¿Qué impacto tendría si funciona bien?" hint="Ahorro, velocidad, calidad, otro">
              <Textarea
                value={f.impacto}
                onChange={(e) => set('impacto', e.target.value)}
                placeholder="Ej: Reduciría en un 50% el tiempo de clasificación de casos..."
                rows={2}
              />
            </Field>
            <Field label="¿Los datos que necesita ya existen en algún sistema? ¿Cuál?">
              <Textarea
                value={f.datos_existentes}
                onChange={(e) => set('datos_existentes', e.target.value)}
                placeholder="Ej: Sí, en el sistema de ficha clínica..."
                rows={2}
              />
            </Field>
            <Field label="¿Hay alguien en tu equipo que lideraría el uso en el día a día?">
              <Input
                value={f.lider_uso}
                onChange={(e) => set('lider_uso', e.target.value)}
                placeholder="Nombre y rol"
              />
            </Field>
            <Field label="¿Hay algo que te preocupe de esta iniciativa?" hint="Riesgo, resistencia, complejidad">
              <Textarea
                value={f.preocupaciones}
                onChange={(e) => set('preocupaciones', e.target.value)}
                placeholder="Ej: Me preocupa la privacidad de los datos de pacientes..."
                rows={2}
              />
            </Field>
          </CardContent>
        </Card>

        {/* Section 6: Algo más */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">6. ¿Algo más que quieras agregar?</h3>
            <Field label="Contexto adicional, urgencia, dependencias o cualquier otra información">
              <Textarea
                value={f.contexto_adicional}
                onChange={(e) => set('contexto_adicional', e.target.value)}
                placeholder="Cualquier detalle adicional que nos ayude a entender mejor tu idea..."
                rows={3}
              />
            </Field>
          </CardContent>
        </Card>

        {/* What happens next + submit */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">¿Qué pasa después?</h3>
            <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside mb-5">
              <li>Recibimos tu solicitud y acusamos recibo en máximo 5 días hábiles.</li>
              <li>Evaluamos la iniciativa internamente (valor, factibilidad, riesgo).</li>
              <li>Si avanza, te contactamos para profundizar en los detalles.</li>
              <li>Las iniciativas aprobadas ingresan al portafolio con seguimiento.</li>
            </ol>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSubmit}
                disabled={submitting || !f.problema.trim()}
                className="bg-primary hover:bg-primary/90 text-primary-foreground">
                {submitting ? (
                  <>
                    <HugeiconsIcon icon={Loading02Icon} size={16} strokeWidth={1.5} className="animate-spin mr-1.5" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} strokeWidth={1.5} className="mr-1.5" />
                    Enviar solicitud
                  </>
                )}
              </Button>
              <span className="text-[11px] text-muted-foreground">Tu progreso se guarda automáticamente</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
